import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ClipboardPaste, FileCheck2, FileText, FileUp, FolderPlus, History, TriangleAlert, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { create } from "zustand";

import { api } from "../../api/client";
import type { Exercise, OpoDraftState, OpoImportPreview, OpoImportWarning } from "../../api/types";
import { designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Dialog, DialogBody } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { PropRow } from "../../components/panel";
import { Motion, motion, Presence } from "../../components/presence";
import { Badge, Button, Field, IconButton, SectionLabel, textareaClass } from "../../components/ui";
import { genericEnemyLabel, isNeutral } from "../../enemyDisplay";
import { k, t, tServer } from "../../i18n/i18n";
import { missionName } from "../../i18n/vocabulary";
import { seasonalLabel } from "../../weather";
import { formatDate, formatDateTime } from "../../licence/licence";
import { levelLabel, missionLabel } from "../../missions";
import { fileSize, MAX_BYTES } from "../../views/received/ImportDialog";
import { OpoVerificationDialog } from "./OpoVerification";
import { Waiting } from "../../components/Waiting";

/** Import d'un OPO (docs/20-import-opo.md). Un OPO produit par Generex — web ou iPad — recrée son dossier à
 *  l'identique (étape 1) : le fichier choisi est lu par le serveur, qui en montre le récapitulatif (émetteur,
 *  terrain, forces, coordination, ordre) et les avertissements ; « Créer le dossier », l'action attendue, crée le
 *  dossier dans la licence du poste, le fichier rangé dans ses ordres reçus. Les avertissements viennent juste sous
 *  le fichier, avant le récapitulatif (icône du fichier orangée s'il y en a), et l'erreur d'une création refusée
 *  s'affiche en tête, où elle se voit. Un OPO sans données Generex (ordre rédigé par un état-major, OPO enregistré
 *  de nouveau dans un traitement de texte, texte collé) passe par l'écran de vérification (étape 2,
 *  `OpoVerificationDialog`) ; un import en cours de vérification se reprend depuis cette fenêtre. Un fichier
 *  .generex déposé ici est rendu à l'import des dossiers. Ouverte depuis l'accueil ou le menu des dossiers de la
 *  barre latérale (`useOpoImport`). */

/** Formats acceptés : ceux qu'exporte Generex (PDF, Word, LibreOffice), RTF et texte (ordres d'état-major). */
export const OPO_ACCEPTED = ".pdf,.docx,.doc,.odt,.rtf,.txt,application/pdf,application/msword";

type Scope = "home" | "app";

/** Fenêtre ouverte, et dans quel plan : l'accueil (au-dessus de l'application) ou l'espace de travail ; `file` : fichier
 *  déjà choisi (import de l'accueil, qui reçoit aussi les fichiers .generex), lu dès l'ouverture. */
export const useOpoImport = create<{
  openIn: Scope | null;
  file: File | null;
  /** Choix de l'utilisateur (08/10/2026) : importer et peupler la carte, ou aussi décliner l'OPO aux échelons
   *  inférieurs (ordres N-1, et N-2 pour une division), aussitôt le dossier créé. */
  decline: boolean;
  open: (scope: Scope, file?: File | null) => void;
  setDecline: (decline: boolean) => void;
  close: () => void;
}>((set) => ({
  openIn: null,
  file: null,
  decline: false,
  open: (scope, file = null) => set({ openIn: scope, file }),
  setDecline: (decline) => set({ decline }),
  close: () => set({ openIn: null, file: null, decline: false }),
}));

/** Choix de ce que fait l'import, en tête de la fenêtre : deux cartes, une seule cochée. */
function ImportMode() {
  const decline = useOpoImport((s) => s.decline);
  const setDecline = useOpoImport((s) => s.setDecline);
  const options = [
    {
      value: false,
      title: t("Importer l'OPO"),
      text: t("Le dossier est créé, carte peuplée : fuseau, LIMA, unités amies et ennemies, missions."),
    },
    {
      value: true,
      title: t("Importer et décliner"),
      text: t("Même chose, puis les ordres des échelons inférieurs sont rédigés : OPO de brigade → GTIA, de division → brigades et GTIA, de GTIA → S/GTIA."),
    },
  ];
  return (
    <div role="radiogroup" aria-label={t("Ce que fait l'import")} className="grid gap-2 sm:grid-cols-2">
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={decline === o.value}
          onClick={() => setDecline(o.value)}
          className={clsx(
            // Contenu calé en haut : un bouton centre verticalement son contenu, les deux titres seraient décalés.
            "flex flex-col items-start justify-start rounded-xl border px-3.5 py-3 text-left transition-colors",
            decline === o.value ? "border-accent bg-accent-soft" : "border-line hover:border-accent hover:bg-hover",
          )}
        >
          <span className="flex items-center gap-2 text-ui font-medium text-ink">
            <span
              aria-hidden
              className={clsx(
                "grid size-4 shrink-0 place-items-center rounded-full border",
                decline === o.value ? "border-accent" : "border-line-strong",
              )}
            >
              {decline === o.value && <span className="size-2 rounded-full bg-accent" />}
            </span>
            {o.title}
          </span>
          <span className="mt-1 block text-meta leading-snug text-ink-muted">{o.text}</span>
        </button>
      ))}
    </div>
  );
}

/** Fichier de dossier (.generex, ou son JSON) plutôt qu'un OPO : l'import de l'accueil reçoit les deux. */
export function isDossierFile(file: Pick<File, "name" | "type">): boolean {
  return /\.(generex|json)$/i.test(file.name) || file.type === "application/json";
}

/** Paragraphes de l'ordre (clés du découpage du serveur), pour dire ce qui a été retouché. */
const SECTION_LABELS: Record<string, string> = {
  preamble: k("En-tête"),
  situation: k("§ 1 Situation"),
  enemy: k("§ 1.a Ennemi"),
  friendly: k("§ 1.b Amis"),
  mission: k("§ 2 Mission"),
  intention: k("Intention du chef"),
  execution: k("§ 3 Exécution"),
  logistics: k("§ 4 Soutien logistique"),
  command: k("§ 5 Commandement et transmissions"),
  annex: k("Annexes"),
  all: k("Texte de l'ordre"),
};

export function sectionsLabel(sections: string[] | undefined): string {
  const labels = (sections ?? []).map((s) => t(SECTION_LABELS[s] ?? SECTION_LABELS.all!));
  return [...new Set(labels)].join(", ") || t(SECTION_LABELS.all!);
}

/** Avertissement du récapitulatif : ton, titre et explication, dans la langue de l'interface. */
export function warningText(w: OpoImportWarning): { tone: "warn" | "info"; title: string; text: string } {
  switch (w.code) {
    case "retouched":
      return {
        tone: "warn",
        title: t("Document retouché après l'export"),
        text: t("Modifié depuis l'export : {sections}. La carte et l'ordre du dossier viennent des données d'origine du fichier ; relisez l'ordre. Le document retouché est rangé dans les ordres reçus.", { sections: sectionsLabel(w.sections) }),
      };
    case "odb_copied":
      return {
        tone: "info",
        title: t("Ordre de bataille copié dans votre licence"),
        text: t("« {name} », construit par l'émetteur, est recopié dans votre licence avec le dossier.", { name: w.name ?? "" }),
      };
    case "eni_odb_copied":
      return {
        tone: "info",
        title: t("Ordre de bataille ennemi copié dans votre licence"),
        text: t("« {name} » est recopié dans votre licence avec le dossier.", { name: w.name ?? "" }),
      };
    case "zone_recreated":
      return {
        tone: "info",
        title: t("Terrain libre recréé"),
        text: t("Le terrain « {name} » se prépare de nouveau dans votre licence : la carte apparaît quand il est prêt, en quelques minutes.", { name: w.name ?? "" }),
      };
    case "unit_missing":
      return {
        tone: "warn",
        title: t("Unités introuvables"),
        text: t("{units} : absentes des ordres de bataille de ce serveur, elles ne sont pas placées. Vérifiez le dispositif après la création.", { units: (w.units ?? []).join(", ") }),
      };
    case "engine":
      return {
        tone: "info",
        title: t("Ordre rédigé par une autre version de Generex"),
        text: t("L'ordre se met à jour à sa première ouverture ; les paragraphes modifiés à la main sont conservés."),
      };
    case "ipad":
      return {
        tone: "info",
        title: t("OPO de Generex pour iPad"),
        text: t("La carte est recréée : camp, unités à leurs positions du temps {phase} de l'iPad, rôles, missions, déplacements et ponts détruits. Les autres temps suivent le dispositif de Generex Web et les symboles de mission sont redessinés d'après les missions. L'ordre se rédige ensuite dans Generex Web ; le PDF de l'iPad reste dans les ordres reçus.", { phase: w.phase ?? "T1" }),
      };
    case "mission_unknown":
      return {
        tone: "warn",
        title: t("Mission à choisir"),
        text: t("« {name} » ne fait pas partie des missions de ce niveau : choisissez la mission dans les Données initiales.", { name: w.name ?? "" }),
      };
    case "grouped":
      return {
        tone: "info",
        title: t("Document regroupé des ordres subordonnés"),
        text: t(
          "Ce document réunit les ordres de {n} subordonnés. Le dossier recréé est celui de l'échelon qui les a donnés : générez de nouveau les ordres subordonnés depuis ce dossier.",
          { n: w.items ?? 0 },
        ),
      };
    default:
      return { tone: "info", title: "", text: "" };
  }
}

function terrainLabel(p: OpoImportPreview): string {
  const terrain = p.terrain;
  if (!terrain) return "—";
  if (terrain.kind === "zone") return t("Terrain libre « {name} »", { name: terrain.name ?? "" });
  return terrain.name ?? "—";
}

/** Récapitulatif d'un OPO importé, par rubriques (elles deviendront les onglets de l'écran de vérification). */
function Recap({ preview }: { preview: OpoImportPreview }) {
  const enemy = isNeutral(preview.reference_enemy) ? genericEnemyLabel() : (preview.enemy_type ?? "—");
  const lima = preview.lima ?? [];
  const phases = preview.phases ?? [];
  return (
    <Motion origin="fade" className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <p className="text-title font-semibold text-ink">
          {preview.order ? t("OPO n° {n}", { n: preview.order.number }) : t("OPO")}
          {preview.issuer && <span className="font-normal text-ink-soft"> {t("de {issuer}", { issuer: preview.issuer })}</span>}
        </p>
        {preview.order?.status === "finalized" && <Badge tone="ok">{t("Validé")}</Badge>}
        {preview.source === "ipad" && <Badge>{t("Generex pour iPad")}</Badge>}
      </div>
      <section>
        <SectionLabel>{t("Émetteur et niveau")}</SectionLabel>
        <div className="mt-1.5 rounded-lg border border-line px-3">
          <PropRow label={t("Nom du dossier")}>{preview.name}</PropRow>
          <PropRow label={t("Niveau de l'ordre")}>{levelLabel(preview.level)}</PropRow>
          <PropRow label={t("Force commandée")}>{preview.force ?? "—"}</PropRow>
          <PropRow label={t("Mission")}>{preview.mission ? missionName(missionLabel(preview.mission)) : "—"}</PropRow>
        </div>
      </section>
      <section>
        <SectionLabel>{t("Terrain et forces")}</SectionLabel>
        <div className="mt-1.5 rounded-lg border border-line px-3">
          <PropRow label={t("Terrain")}>{terrainLabel(preview)}</PropRow>
          {preview.weather && (
            // Météo que le dossier appliquera, ramenée au terrain (scénario de l'iPad sans ciel compris).
            <PropRow label={t("Météo")}>{preview.weather.key === "auto" ? seasonalLabel() : preview.weather.label}</PropRow>
          )}
          <PropRow label={t("Ennemi")}>{enemy}</PropRow>
          <PropRow label={t("Unités placées")}>
            {preview.units
              ? t("{ami} amies · {eni} ennemies", { ami: preview.units.ami, eni: preview.units.eni })
              : "—"}
          </PropRow>
        </div>
      </section>
      <section>
        <SectionLabel>{t("Coordination")}</SectionLabel>
        <div className="mt-1.5 rounded-lg border border-line px-3">
          <PropRow label={t("LIMA")}>
            {lima.length ? (
              <span className="inline-flex flex-col items-end gap-0.5">
                {lima.map((l) => (
                  <span key={l.name} className="whitespace-nowrap">
                    <span className="font-medium">{l.name}</span>
                    {l.dtg && <span className="ml-2 font-mono text-ink-soft tabular-nums">{l.dtg}</span>}
                  </span>
                ))}
              </span>
            ) : (
              "—"
            )}
          </PropRow>
          <PropRow label={t("Temps de la manœuvre")}>{phases.length ? phases.join(" · ") : "—"}</PropRow>
        </div>
      </section>
      {preview.order && (
        <section>
          <SectionLabel>{t("Ordre")}</SectionLabel>
          <div className="mt-1.5 rounded-lg border border-line px-3">
            <PropRow label={t("Langue de l'ordre")}>{preview.language === "en" ? t("Anglais") : t("Français")}</PropRow>
            <PropRow label={t("Paragraphes modifiés à la main")}>{preview.order.edited}</PropRow>
          </div>
        </section>
      )}
    </Motion>
  );
}

/** Avertissements du récapitulatif, sous le fichier. */
function Warnings({ warnings }: { warnings: OpoImportWarning[] }) {
  return (
    <div className="space-y-2">
      {warnings.map((w, i) => {
        const text = warningText(w);
        return (
          <Callout key={`${w.code}-${i}`} tone={text.tone} title={text.title}>
            {text.text}
          </Callout>
        );
      })}
    </div>
  );
}

/** Dossier ouvert dont les ordres reçus accueillent un OPO sans données Generex (barre latérale). */
export interface ReceivedTarget {
  name: string;
  file: (file: File) => Promise<void>;
}

export function ImportOpoDialog({
  initialFile = null,
  onClose,
  onCreated,
  onDossierFile,
}: {
  initialFile?: File | null;
  onClose: () => void;
  onCreated: (ex: Exercise) => void | Promise<void>;
  /** Fichier .generex déposé ou choisi ici : rendu à l'import des dossiers. */
  onDossierFile?: (file: File) => void;
  /** Dossier ouvert (barre latérale) : gardé pour compatibilité ; un OPO sans données Generex passe désormais par la
   *  vérification. */
  received?: ReceivedTarget | null;
  /** Accueil : gardé pour compatibilité (voir `received`). */
  onChooseDossier?: () => void;
}) {
  const queryClient = useQueryClient();
  const [verification, setVerification] = useState<OpoDraftState | null>(null);
  const [paste, setPaste] = useState<string | null>(null);
  const drafts = useQuery({ queryKey: ["opo-drafts"], queryFn: api.opoDrafts, staleTime: 10_000 });
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<OpoImportPreview | null>(null);
  const [busy, setBusy] = useState<"read" | "create" | "verify" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  // « Créer le dossier » sans fichier : l'action attendue (la zone de dépôt) est désignée et dite dans la fenêtre (un avis
  // de l'application passerait sous son voile).
  const [hint, setHint] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const target = useRef<HTMLButtonElement>(null);
  const failure = useRef<HTMLDivElement>(null);

  const analyse = async (chosen: File) => {
    setHint(false);
    setError(null);
    if (isDossierFile(chosen) && onDossierFile) {
      // Fichier .generex : il recrée son dossier par l'import des dossiers, sans passer par le récapitulatif.
      onDossierFile(chosen);
      return;
    }
    setFile(chosen);
    setPreview(null);
    if (chosen.size > MAX_BYTES) {
      setError(t("Fichier trop volumineux : 15 Mo au plus."));
      return;
    }
    if (isDossierFile(chosen)) {
      setError(t("Ce fichier est un dossier Generex (.generex) : importez-le avec « Importer un OPO » de l'accueil, ou « Importer un dossier… » du menu des dossiers."));
      return;
    }
    setBusy("read");
    try {
      const seen = await api.previewOpoImport(chosen);
      setPreview(seen);
      // Sans données Generex : la lecture du texte s'ouvre aussitôt dans l'écran de vérification.
      if (seen.pending === "verification") await verify(chosen);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Lecture impossible : le serveur ne répond pas."));
    } finally {
      setBusy(null);
    }
  };

  /** Vérification ouverte : fichier sans données Generex, ou texte collé. */
  const verify = async (source: File | string) => {
    setBusy("verify");
    setError(null);
    try {
      setVerification(await (typeof source === "string" ? api.pasteOpoDraft(source) : api.openOpoDraft(source)));
      void queryClient.invalidateQueries({ queryKey: ["opo-drafts"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Lecture impossible : le serveur ne répond pas."));
    } finally {
      setBusy(null);
    }
  };

  const resume = async (id: string) => {
    setBusy("verify");
    setError(null);
    try {
      setVerification(await api.opoDraft(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Lecture impossible : le serveur ne répond pas."));
      void queryClient.invalidateQueries({ queryKey: ["opo-drafts"] });
    } finally {
      setBusy(null);
    }
  };

  const createDossier = async () => {
    if (!file) return;
    setBusy("create");
    setError(null);
    try {
      await onCreated(await api.importOpo(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : t("Création impossible : le serveur ne répond pas."));
    } finally {
      setBusy(null);
    }
  };

  const other = () => {
    setFile(null);
    setPreview(null);
    setError(null);
    input.current?.click();
  };

  // Fichier déjà choisi (import de l'accueil) : lu dès l'ouverture, une fois.
  const started = useRef(false);
  useEffect(() => {
    if (initialFile && !started.current) {
      started.current = true;
      void analyse(initialFile);
    }
  }, [initialFile]);

  // Erreur (création refusée, fichier illisible) : affichée en tête du corps et amenée en vue, où qu'on ait défilé.
  useEffect(() => {
    if (!error) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const frame = window.requestAnimationFrame(() =>
      failure.current?.scrollIntoView?.({ block: "nearest", behavior: reduced ? "auto" : "smooth" }),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [error]);

  const ready = Boolean(preview?.ready);
  const warnings = preview?.warnings ?? [];
  const cautious = warnings.some((w) => warningText(w).tone === "warn");
  const busyLabel =
    busy === "read" ? <Waiting>{t("Lecture de l'OPO…")}</Waiting> : busy === "create" ? <Waiting>{t("Création du dossier…")}</Waiting> : <Waiting>{t("Analyse de l'ordre…")}</Waiting>;

  if (verification) {
    return (
      <OpoVerificationDialog
        initial={verification}
        onClose={() => {
          void queryClient.invalidateQueries({ queryKey: ["opo-drafts"] });
          onClose();
        }}
        onCreated={async (ex) => {
          void queryClient.invalidateQueries({ queryKey: ["opo-drafts"] });
          await onCreated(ex);
        }}
      />
    );
  }

  // Pied : l'action attendue est le bouton principal, qui pulse (création, ou rangement d'un OPO sans données).
  let primary: ReactNode = (
    <Button variant="secondary" onClick={onClose}>
      {t("Fermer")}
    </Button>
  );
  if (busy !== null && busy !== "read") {
    primary = (
      <Button disabled>
        <Spinner decorative /> {busyLabel}
      </Button>
    );
  } else if (busy === "read" || ready) {
    primary = (
      <Button
        onClick={() => void createDossier()}
        disabled={busy !== null}
        highlight={ready && busy === null}
        data-attention="opo-import-create"
      >
        {busy ? <Spinner decorative /> : <FolderPlus size={14} />}
        {busy ? busyLabel : t("Créer le dossier")}
      </Button>
    );
  }

  return (
    <Dialog
      title={t("Importer un OPO")}
      subtitle={t("Un OPO de Generex (web ou iPad) recrée son dossier à l'identique ; un ordre d'état-major est lu, vérifié par vous, puis crée son dossier, carte peuplée.")}
      width={560}
      onClose={onClose}
      footer={
        paste !== null && !file ? (
          <>
            <Button variant="secondary" onClick={() => setPaste(null)} disabled={busy !== null}>
              {t("Retour")}
            </Button>
            <Button
              onClick={() => void verify(paste)}
              disabled={busy !== null}
              highlight={paste.trim().length >= 40 && busy === null}
              blocked={paste.trim().length < 40}
              onBlocked={() => designate("[data-paste-opo]", "field")}
            >
              {busy ? <Spinner decorative /> : <FileText size={14} />}
              {busy ? busyLabel : t("Lire et vérifier")}
            </Button>
          </>
        ) : file ? (
          <>
            <Button variant="secondary" onClick={other} disabled={busy !== null}>
              {t("Autre fichier")}
            </Button>
            {primary}
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {t("Annuler")}
            </Button>
            {/* Sans fichier (R12) : le bouton reste cliquable et désigne la zone de dépôt. */}
            <Button
              blocked
              onBlocked={() => {
                designate(target.current, "ring");
                setHint(true);
              }}
            >
              <FolderPlus size={14} /> {t("Créer le dossier")}
            </Button>
          </>
        )
      }
    >
      <DialogBody className="space-y-4">
        <ImportMode />
        {paste !== null && !file ? (
          <Motion origin="fade" className="space-y-2">
            <Field label={t("Texte de l'ordre")} hint={t("Collez le texte entier de l'OPO : en-tête, paragraphes 1 à 5. Rien n'est créé avant la vérification.")}>
              <textarea
                data-paste-opo=""
                className={clsx(textareaClass, "min-h-56")}
                value={paste}
                autoFocus
                onChange={(e) => setPaste(e.target.value)}
                placeholder={t("Collez ici le texte de l'OPO…")}
              />
            </Field>
          </Motion>
        ) : file ? (
          <div className={clsx("flex items-center gap-3 rounded-xl border border-line bg-canvas/50 px-3.5 py-3", motion("fade"))}>
            {ready && cautious ? (
              <TriangleAlert size={20} className="shrink-0 text-warn" aria-label={t("À vérifier avant la création")} />
            ) : ready ? (
              <FileCheck2 size={20} className="shrink-0 text-ok" />
            ) : (
              <FileText size={20} className="shrink-0 text-ink-soft" />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate text-ui font-medium text-ink">{file.name}</p>
              <p className="text-meta text-ink-muted">
                {fileSize(file.size)}
                {busy === "read" && (
                  <>
                    {" — "}
                    <Waiting>{t("lecture en cours…")}</Waiting>
                  </>
                )}
              </p>
            </div>
            <IconButton size="sm" onClick={other} disabled={busy !== null} title={t("Choisir un autre fichier")} aria-label={t("Choisir un autre fichier")}>
              <X size={16} />
            </IconButton>
          </div>
        ) : (
          <button
            ref={target}
            type="button"
            onClick={() => input.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => setOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setOver(false);
              const dropped = e.dataTransfer.files?.[0];
              if (dropped) void analyse(dropped);
            }}
            className={clsx(
              "flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors",
              over ? "border-accent bg-accent-soft" : "border-line-strong hover:border-accent hover:bg-hover",
            )}
          >
            <FileUp size={24} className="text-ink-soft" />
            <span className="text-ui font-medium text-ink">{t("Déposez le fichier de l'OPO, ou cliquez pour le choisir")}</span>
            <span className="text-meta leading-snug text-ink-muted">
              {t("PDF, Word (.docx ou .doc), LibreOffice (.odt), RTF ou texte — OPO de Generex (web ou iPad) ou ordre d'état-major, {max} au plus.", {
                max: fileSize(MAX_BYTES).replace(" ", "\u00a0"),
              })}
            </span>
          </button>
        )}

        {!file && paste === null && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button variant="link" onClick={() => setPaste("")}>
              <ClipboardPaste size={14} /> {t("Coller le texte de l'ordre")}
            </Button>
            <span className="text-meta text-ink-muted">{t("PDF numérisés et photos : non lus.")}</span>
          </div>
        )}


        <Presence show={!file && paste === null && Boolean(drafts.data?.length)}>
          {!file && paste === null && drafts.data?.length ? (
            <Motion origin="fade">
              <SectionLabel>{t("Imports en cours de vérification")}</SectionLabel>
              <div className="mt-1.5 rounded-lg border border-line">
                {drafts.data.map((d) => (
                  <div key={d.id} className="flex min-h-10 items-center gap-3 border-b border-line px-3 py-2 last:border-b-0">
                    <History size={16} className="shrink-0 text-ink-soft" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-ui font-medium text-ink">{d.name}</p>
                      <p className="truncate text-meta text-ink-muted">
                        {[
                          d.filename ?? t("Texte collé"),
                          d.updated_at ? t("enregistré le {date}", { date: formatDateTime(d.updated_at) }) : "",
                          t("conservé jusqu'au {date}", { date: formatDate(d.expires_at) }),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" onClick={() => void resume(d.id)} disabled={busy !== null}>
                      {t("Reprendre")}
                    </Button>
                  </div>
                ))}
              </div>
            </Motion>
          ) : null}
        </Presence>

        <Presence show={Boolean(error)}>
          {error && (
            <div ref={failure}>
              <Callout tone="danger">{error}</Callout>
            </div>
          )}
        </Presence>

        <Presence show={hint && !file}>
          {hint && !file && <Callout tone="info">{t("Choisissez d'abord le fichier de l'OPO.")}</Callout>}
        </Presence>

        <Presence show={busy === "read" || busy === "verify"}>
          {(busy === "read" || busy === "verify") && (
            <Motion origin="fade" className="flex items-center gap-2 text-ui text-ink-soft">
              <Spinner decorative />{" "}
              {busy === "read" ? <Waiting>{t("Lecture de l'OPO et du dossier qu'il décrit…")}</Waiting> : <Waiting>{t("Analyse de l'ordre : unités, lieux, fuseau et carte…")}</Waiting>}
            </Motion>
          )}
        </Presence>

        {/* Avertissements d'abord : document retouché, unités introuvables… se voient avant « Créer le dossier ». */}
        <Presence show={warnings.length > 0}>{warnings.length > 0 && <Warnings warnings={warnings} />}</Presence>

        {preview && ready && <Recap preview={preview} />}

        <Presence show={Boolean(preview?.notes?.length)}>
          {preview?.notes?.length ? (
            <Callout tone="info" title={t("La relecture du dossier signale")}>
              <ul className="list-disc space-y-0.5 pl-4">
                {preview.notes.map((note) => (
                  <li key={note}>{tServer(note)}</li>
                ))}
              </ul>
            </Callout>
          ) : null}
        </Presence>

        <Presence show={ready}>
          {ready && (
            <Motion origin="fade">
              <p className="text-ui leading-relaxed text-ink-soft">
                {t("Le dossier rejoint votre licence ; le fichier importé est rangé dans ses ordres reçus.")}
              </p>
            </Motion>
          )}
        </Presence>

      </DialogBody>
      <input
        ref={input}
        type="file"
        accept={onDossierFile ? `${OPO_ACCEPTED},.generex,.json,application/json` : OPO_ACCEPTED}
        className="hidden"
        onChange={(e) => {
          const chosen = e.target.files?.[0];
          e.target.value = "";
          if (chosen) void analyse(chosen);
        }}
      />
    </Dialog>
  );
}
